import React, { useMemo, useRef, useState } from 'react'
import { BreadcrumbView } from '../../../../../context/breadcrumb.context'
import { BadgeInfo, BadgeSuccess, BadgeWarning } from '../../../../../shared/component/badge'
import { Menu, MenuDelete, MenuEdit, MenuExport, MenuItem, MenuRecycle, MenuTableSettingToggle } from '../../../../../shared/menu/menu'
import { CardFullHeightScrollY } from '../../../../../shared/card/card-full-height-scroll-y'
import { useApiListSelfRegister } from '../../../../../api-new/state/content'
import { IconActivityLog, IconCopy, IconLaunch, IconPlus, IconTradeShow } from '../../../../../shared/icon/icon'
import { useAuditCapabilities } from '../../../../../shared/audit/use-audit-capabilities'
import { ModalDialog, useModalActions } from '../../../../../shared/component/modal-dialog'
import { CopyValue } from '../../../../../shared/sso-workspace/copy-value'
import '../../../../../shared/sso-workspace/sso-workspace.scss'
import { useRoleOrgSelfRegister } from '../../../../../role/hook/role-org'
import { TableAgClient } from '../../../../../shared/table-builder/table-ag-client'
import { ColumnText, ColumnDate, ColumnMenu, ColumnSelector } from '../../../../../shared/table-builder/table-columns'

export const SelfRegisterList = () => {
  const apiListSelfRegister = useApiListSelfRegister()

  const modalActions = useModalActions('copylink')

  const roleOrgSelfRegister = useRoleOrgSelfRegister()
  const caps = useAuditCapabilities()

  const handleReady = async () => await apiListSelfRegister.get()
  const handleDestroyed = () => apiListSelfRegister.init()

  const [copyIdentifier, updateCopyIdentifier] = useState()

  const handleCopy = identifier => {
    updateCopyIdentifier(identifier)
    modalActions.show()
  }

  const handleOpen = identifier => window.open(`/external/self-register/${identifier}`)

  const tableRef = useRef()
  const handleExportCsv = () => tableRef.current.exportCsv('tradeshows')
  const handleTableSettingToggle = () => tableRef.current.toggleSideBar()

  const HeaderMenu = () =>
    <Menu>
      <MenuItem text="add" Icon={IconPlus} to="/org/self-register/list/add" show={roleOrgSelfRegister.accessCreate} />
      <MenuExport onClick={handleExportCsv} show />
      <MenuTableSettingToggle onClick={handleTableSettingToggle} show />
      <MenuItem Icon={IconActivityLog} text="audit log" to="/org/audit-log?type=self_registers" show={caps.canAudit} />
      <MenuRecycle to="/org/self-register/list/recycle" show={roleOrgSelfRegister.accessDelete} />
    </Menu>

  return (
    <React.Fragment>
      <CopyLinkModal identifier={copyIdentifier} />
      <BreadcrumbView Icon={IconTradeShow} text="self register">
        <CardFullHeightScrollY title="self register" HeaderMenu={HeaderMenu}>
          <TableAgClient
            ref={tableRef}
            apiList={apiListSelfRegister}
            onReady={handleReady}
            onDestoryed={handleDestroyed}
            emptyState={{
              Icon: IconTradeShow,
              title: 'No self-register links yet',
              message: 'Add a self-register link to let people sign themselves up for your organization.',
              action: { label: 'Add link', Icon: IconPlus, to: '/org/self-register/list/add', show: roleOrgSelfRegister.accessCreate }
            }}>
            <ColumnText field="name" />
            <ColumnDate field="startOn" header="starts on" />
            <ColumnDate field="endOn" header="ends on" />
            <ColumnSelector field="status" selector={row => <TradeShowStatus tradeShow={row} />} />
            <ColumnMenu
              menu={row =>
                <Menu small>
                  <MenuEdit to={`/org/self-register/list/edit/${row.id}`} show={roleOrgSelfRegister.accessUpdate} />
                  <MenuItem Icon={IconCopy} text="copy link" onClick={() => handleCopy(row.identifier)} show={roleOrgSelfRegister.accessRead} />
                  <MenuItem Icon={IconLaunch} text="launch" onClick={() => handleOpen(row.identifier)} show={roleOrgSelfRegister.accessRead} />
                  <MenuDelete to={`/org/self-register/list/edit/${row.id}/delete`} show={roleOrgSelfRegister.accessDelete} />
                </Menu>}
            />
          </TableAgClient>
        </CardFullHeightScrollY>
      </BreadcrumbView>
    </React.Fragment>
  )
}

const TradeShowStatus = ({ tradeShow }) => {
  const status = useMemo(
    () => {
      let now = Date.now()
      let startOn = Date.parse(tradeShow.startOn)
      let endOn = Date.parse(tradeShow.endOn)

      if (tradeShow.closed) {
        return <BadgeInfo value="closed" />
      } else if (!startOn && !endOn) {
        return <BadgeSuccess value="open" />
      } else if (tradeShow.activeUntilEnd && now < endOn) {
        return <BadgeSuccess value="open" />
      } else if (now > startOn && now < endOn) {
        return <BadgeSuccess value="open" />
      } else {
        return <BadgeWarning value="inactive" />
      }
    },
    [tradeShow]
  )

  return (
    <React.Fragment>
      {status}
    </React.Fragment>
  )
}

const CopyLinkModal = ({ identifier }) => {
  // const url = useMemo(() => `${process.env.REACT_APP_SITE}/external/self-register/${identifier}?email=EMAIL&first-name=FIRST_NAME&last-name=LAST_NAME`, [
  //   identifier
  // ])

  const url = useMemo(() => `${process.env.REACT_APP_SITE}/external/self-register/${identifier}`, [identifier])

  // The shared CopyValue (owner 2026-10-07): Copy that Safari allows, and
  // Open, so whoever copies the registration page can also try it.
  return (
    <ModalDialog target="copylink" header="Open Link">
      <div className="sw-workspace">
        <CopyValue id="self-register-link" label="Registration link" value={url} openHref={url} />
      </div>

      {/* <table className="table">
        <tbody>
          <tr>
            <th>Argument</th>
            <th>Type</th>
          </tr>
          <tr>
            <td>email</td>
            <td>string</td>
          </tr>
          <tr>
            <td>first-name</td>
            <td>string</td>
          </tr>
          <tr>
            <td>last-name</td>
            <td>string</td>
          </tr>
        </tbody>
      </table> */}
    </ModalDialog>
  )
}
